import { PAP_Delete_StackSection } from "@mirage/analytics/events/types/delete_stack_section";
import { PAP_Rename_StackSection } from "@mirage/analytics/events/types/rename_stack_section";
import { PAP_Select_StackSection } from "@mirage/analytics/events/types/select_stack_section";
import { upsertStack } from "@mirage/service-stacks";
import { MAX_STACK_SECTION_COUNT } from "@mirage/shared/util/constants";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import {
  activeStackActionAtom,
  activeStackAtom,
  activeStackHasWritePermissionsAtom,
  activeStackMutationRequestIdAtom,
  activeStackSectionIdAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/ActiveStack/atoms";
import i18n from "@mirage/translations";
import classnames from "classnames";
import { useAtom, useAtomValue } from "jotai";
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useReportStackPapEvent } from "../useReportStackPapEvent";
import { SectionDropTargetWrapper } from "./DragAndDrop/SectionDropTargetWrapper";
import { useCreateStackSection } from "./hooks";
import { CreateSectionButton } from "./Sections/CreateSectionButton";
import { useSectionsDragDrop } from "./Sections/DragDrop";
import { NavigationItem } from "./Sections/NavigationItem";
import { InlineCreateSection, NavigationList } from "./Sections/NavigationList";
import styles from "./StackSideNavigator.module.css";
import { SectionsActionType } from "./types";
import { asStackUpsertId } from "./utils";

import type { stacks } from "@dropbox/api-v2-client";
import type { DragDropListItem } from "@mirage/drag-and-drop/DragDropList";

/**
 * This is just a straight copy of the Navigation component, but with the
 * styles updated to match the new design.
 */
export const StackSideNavigator = ({
  scrollToSection,
}: {
  scrollToSection: (sectionId: string) => void;
}) => {
  const reportStackPapEvent = useReportStackPapEvent();

  const stack = useAtomValue(activeStackAtom);
  const [{ allSections: sections }, setSections] = useAtom(
    activeStackSectionsAtom,
  );
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);
  const [currentAction, setCurrentAction] = useAtom(activeStackActionAtom);
  const currentSectionId = useAtomValue(activeStackSectionIdAtom);

  const onCreateSection = useCreateStackSection();
  const { otherSections, onDrop } = useSectionsDragDrop();
  const mutationId = useAtomValue(activeStackMutationRequestIdAtom);
  const [isHovered, setIsHovered] = useState(false);
  // There's a strange issue where the value of `currentSectionId` gets frozen
  // inside the drag-and-drop component. Therefore, use a ref to make sure the
  // value is always updated.
  const currentSectionIdRef = useRef<string | null>(null);
  const [, forceRefresh] = useState(false);

  useEffect(() => {
    currentSectionIdRef.current = currentSectionId;
    forceRefresh((v) => !v);
  }, [currentSectionId]);

  const [isEditing, setIsEditing] = useState(false);

  const handleScrollToSection = useCallback(
    (sectionId: string) => {
      scrollToSection(sectionId);
      reportStackPapEvent(PAP_Select_StackSection());
    },
    [reportStackPapEvent, scrollToSection],
  );

  const upsertStackFn = useCallback(
    async (newSections: stacks.Section[]) => {
      const update: stacks.StackDataUpdate = {
        field: {
          ".tag": "section_data_update",
          sections: newSections,
        },
      };
      await upsertStack(asStackUpsertId(stack?.namespace_id, mutationId), [
        update,
      ]);
      setSections(newSections);
    },
    [stack, mutationId, setSections],
  );

  const onRenameSection = useCallback(
    async (newName: string, sectionId: string) => {
      if (!sections) return;

      const newSections = sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            title: newName,
          };
        }
        return section;
      });
      reportStackPapEvent(PAP_Rename_StackSection());
      upsertStackFn(newSections);
    },
    [reportStackPapEvent, sections, upsertStackFn],
  );

  const onDeleteSection = useCallback(
    async (sectionId: string) => {
      if (!sections) return;

      const newSections = sections.filter(
        (section) => section.id !== sectionId,
      );
      reportStackPapEvent(PAP_Delete_StackSection());
      upsertStackFn(newSections);
    },
    [reportStackPapEvent, sections, upsertStackFn],
  );

  const listItems: DragDropListItem[] = useMemo(() => {
    if (!otherSections) {
      return [];
    }

    return otherSections.map((section) => {
      const sectionId = section.id ?? "";
      return {
        id: `${sectionId}-${section.title}-${hasWritePermissions}`,
        key: sectionId,
        render: () => (
          <SectionDropTargetWrapper sectionId={sectionId}>
            <NavigationItem
              key={section.id}
              hasWritePermissions={hasWritePermissions ?? false}
              onDeleteSection={onDeleteSection}
              onRenameSection={onRenameSection}
              scrollToSection={handleScrollToSection}
              section={section}
              showCount={false}
            />
          </SectionDropTargetWrapper>
        ),
      };
    });
  }, [
    handleScrollToSection,
    hasWritePermissions,
    onDeleteSection,
    onRenameSection,
    otherSections,
  ]);

  useEffect(() => {
    // If another section action occurs while creation is in progress, cancel the current action
    if (currentAction !== SectionsActionType.CREATE_SECTION) {
      setIsEditing(false);
    }
  }, [currentAction]);

  // Track whether the user is currently editing or creating a section based on the active stack action
  const isSectionActionInProgress =
    currentAction === SectionsActionType.EDIT_SECTION ||
    currentAction === SectionsActionType.CREATE_SECTION;

  const hasTooManySections = sections
    ? sections.length >= MAX_STACK_SECTION_COUNT
    : false;

  const createButtonDisabled = hasTooManySections || !hasWritePermissions;

  const createButton = !hasWritePermissions ? (
    <DigTooltip title={i18n.t("read_only_section_tooltip")}>
      <div style={{ maxWidth: "150px" }}>
        <CreateSectionButton
          disabled={createButtonDisabled}
          setIsEditing={setIsEditing}
          setCurrentAction={setCurrentAction}
          isAugustRevisionEnabled={false}
        />
      </div>
    </DigTooltip>
  ) : (
    <CreateSectionButton
      disabled={createButtonDisabled}
      setIsEditing={setIsEditing}
      setCurrentAction={setCurrentAction}
      isAugustRevisionEnabled={false}
    />
  );

  const inlineCreateSection = isEditing &&
    currentAction === SectionsActionType.CREATE_SECTION && (
      <InlineCreateSection
        setIsEditing={setIsEditing}
        setCurrentAction={setCurrentAction}
        onCreateSection={onCreateSection}
        isAugustRevisionEnabled
      />
    );

  return (
    <div
      // The menu should remain open when the nav is hovered or an action is in progress
      className={classnames(styles.SideNavContainer, {
        [styles.SideNavContainerHovered]:
          isHovered || isSectionActionInProgress,
      })}
    >
      <SideNavLines
        sections={otherSections ?? []}
        setIsHovered={setIsHovered}
      />
      <div
        className={styles.SideNavOverlay}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        onBlur={(e) => {
          // Hover should remain in effect as long as the user's cursor remains within the
          // boundaries of the overlay or any submenus. Because the section header menu is
          // portaled, we need to perform a separate check as it is not contained within the
          // overlay. This prevents flickering of the dialog when the user triggers an edit
          // action.
          const overlay = e.currentTarget;
          const relatedTarget = e.relatedTarget as Node;
          const sectionHeaderMenu = document.querySelector(
            "#stack_section-header-menu",
          );

          if (
            !overlay.contains(relatedTarget) &&
            !sectionHeaderMenu?.contains(relatedTarget)
          ) {
            setIsHovered(false);
          }
        }}
      >
        <NavigationList
          listItems={listItems}
          hasWritePermissions={!!hasWritePermissions}
          onDrop={onDrop}
          inlineCreateSection={inlineCreateSection}
          createButton={!isEditing ? createButton : undefined}
        />
      </div>
    </div>
  );
};

const SideNavLines = memo(
  ({
    sections,
    setIsHovered,
  }: {
    sections: stacks.Section[];
    setIsHovered: (isHovered: boolean) => void;
  }) => {
    return (
      <div
        className={styles.SideNavLines}
        onMouseOver={() => {
          setIsHovered(true);
        }}
        onFocus={() => {
          setIsHovered(true);
        }}
      >
        {sections.map((section) => (
          <SideNavLine key={section?.id} title={section?.title ?? ""} />
        ))}
        <SideNavLine title="Create Section" />
      </div>
    );
  },
);

SideNavLines.displayName = "SideNavLines";

const SideNavLine = memo(({ title }: { title: string }) => {
  return (
    <div
      role="button"
      tabIndex={0}
      className={styles.SideNavSectionLine}
      style={
        {
          "--side-nav__section-line__width": title
            ? `${Math.min(100, Math.max(30, title.length * 5))}%`
            : "50%",
        } as CSSProperties
      }
    />
  );
});

SideNavLine.displayName = "SideNavLine";
